import { Box, Text, useBreakpointValue } from "@chakra-ui/react";
import { LRPrivText } from "@verivest/logrocket/src";
import { buildRouteUrl, VeriTable } from "@verivest/shared-components";
import { VerificationStatusEnumType } from "@verivest/shared-components/src/api/enums";
import useAccountModel, {
  apiAccountModel,
} from "@verivest/shared-components/src/api/hooks/models/useAccountModel";
import { AccountModel } from "@verivest/shared-components/src/api/models";
import { JsonApiManyResponse } from "@verivest/shared-components/src/api/types";
import Button from "@verivest/shared-components/src/buttons/Button";
import { Renderers } from "@verivest/shared-components/src/components/common/VeriTable/renderers";
import { VeriTableSortDirection } from "@verivest/shared-components/src/components/common/VeriTable/VeriTable.types";
import { Card } from "@verivest/shared-components/src/components/dashboard";
import { VeriHelpToolTip } from "@verivest/shared-components/src/components/popover/VeriHelpToolTip";
import { VeriAsyncRouteComponentType } from "@verivest/shared-components/src/defines/after-js-types";
import { useGetDynamicConfigIsMobile } from "@verivest/shared-components/src/dynamic-config";
import { getIdentityVerificationStatus } from "@verivest/shared-components/src/utils/accounts";
import { ManagerWhiteLabelRouteParams } from "apps/web/src/routes/investorPortalPages.types";
import React from "react";
import { useHistory } from "react-router";
import { InvestorPortalEmptyStatus } from "../../../components/investor-portal/InvestorPortalEmptyStatus";
import { InvestorPortalPageContainer } from "../../../components/investor-portal/layouts/InvestorPortalPageContainer";
import { getPathToEditAccount } from "../../../routes/account/editAccountWizardRoutes";
import { createAccountRoutes } from "../../investor-portal/wizards/account/createAccountRoutes";
import { validateAccountRequiredAttributes } from "../../investor-portal/wizards/investment/select-account/validate-account-required-attributes/validateAccountRequiredAttributes";
import { WhiteLabelPageExtraProps } from "../white-label/types";
import { useIsMobile } from "../../../hooks/useIsMobile";

const DEFAULT_SORT_DIRECTION = VeriTableSortDirection.ASC as const;
const DEFAULT_SORT_FIELD = "name-of-record" as const;
const DEFAULT_PAGE_SIZE = 25 as const;

const privilegedToUploadDocuments = new Set([
  "In progress",
  "Failed",
  "Pending",
  "Pending verification",
]);

const getStatus = (account: AccountModel) => {
  const status = getIdentityVerificationStatus(account, true);
  return (
    (status !== "Verified" &&
      (account.documentsCount || 0) > 0 &&
      "Pending verification") ||
    status ||
    "In progress"
  );
};

const isAllowedToUploadDocuments = (account: AccountModel) => {
  const status = getStatus(account);
  const pureIdentityStatus = getIdentityVerificationStatus(account, true);
  const legacyStatusPass = status && privilegedToUploadDocuments.has(status);
  const statusPass =
    pureIdentityStatus && privilegedToUploadDocuments.has(pureIdentityStatus);

  return (
    account.accountType !== "Joint" &&
    (statusPass || legacyStatusPass) &&
    pureIdentityStatus !== VerificationStatusEnumType.Verified
  );
};

interface AccountsPageServerInitialProps {
  serverDataAccounts?: JsonApiManyResponse<AccountModel>;
}

type AccountsPage = VeriAsyncRouteComponentType<
  WhiteLabelPageExtraProps & AccountsPageServerInitialProps,
  ManagerWhiteLabelRouteParams
>;

const AccountsPage: AccountsPage = ({ serverDataAccounts }) => {
  const history = useHistory();
  const accountModel = useAccountModel({
    forcePlacement: { getMany: false },
  });
  const dynamicIsMobile = useGetDynamicConfigIsMobile();

  const showActionLabel = useBreakpointValue(
    { base: false, md: true },
    dynamicIsMobile ? "base" : "md",
  );
  const accounts = serverDataAccounts?.data;
  const isMobile = useIsMobile();

  return (
    <InvestorPortalPageContainer
      title={"Accounts"}
      titleRight={
        <>
          {!process.env.RAZZLE_APP_DISABLE_ACCOUNT &&
            accounts?.length !== 0 && (
              <Button
                fontWeight="500"
                label="Add new account"
                isLarge={!isMobile}
                to={createAccountRoutes.selectAccount}
              />
            )}
        </>
      }
    >
      <Card bg={"bg.white"} className={"sm:py-3"} isGhost>
        <VeriTable
          tableProps={{ mt: 0 }}
          model={accountModel}
          apiFallbackData={serverDataAccounts}
          columns={{
            nameOfRecord: {
              label: "Account Name",
              processRow: (_, value) => <LRPrivText>{value}</LRPrivText>,
              sortName: "name-of-record",
            },
            accountType: { label: "Account Type", sortable: false },
            identityStatus: {
              props: {
                overflow: "visible",
              },
              label: (
                <Box
                  className={"items-center mr-1.5 sm:mr-0 inline-flex sm:flex"}
                >
                  Identity{" "}
                  <VeriHelpToolTip ml={2}>
                    <Text textStyle={"bodyRegular"}>
                      We run AML/KYC checks to verify your identity
                    </Text>
                  </VeriHelpToolTip>
                </Box>
              ),
              processRow: (_, _identity, account) => {
                const pureIdentityStatus = getIdentityVerificationStatus(
                  account,
                  true,
                );
                return pureIdentityStatus !==
                  VerificationStatusEnumType.Verified
                  ? (account.documentsCount > 0 && "In Review") ||
                      pureIdentityStatus ||
                      "Action needed"
                  : VerificationStatusEnumType.Verified;
              },
              sortable: false,
            },
            /* # Removed as requested in sc-20751 , it will be added back */
            /*accreditationStatus: {
              props: {
                overflow: "visible",
              },
              label: (
                <Flex alignItems={"center"}>
                  Accreditation{" "}
                  <VeriHelpToolTip ml={2}>
                    <Text textStyle={"bodyRegular"}>
                      We review the documents to validate your accreditation
                    </Text>
                  </VeriHelpToolTip>
                </Flex>
              ),
              processRow: (_, _av, account) =>
                getAccreditationVerificationStatus(account),
            },*/
            toDo: {
              sortable: false,
              label: showActionLabel ? "To do" : "",
              processRow: (_, _toDo, account) => {
                const mustUplIdentityDocs = isAllowedToUploadDocuments(account);
                const isValidAcc = validateAccountRequiredAttributes(account);

                const showIdentityDocsBtn = isValidAcc && mustUplIdentityDocs;
                const showCompleteAccBtn = !showIdentityDocsBtn && !isValidAcc;

                return (
                  <>
                    {showIdentityDocsBtn && (
                      <Button
                        width="max-content"
                        outline
                        to={`/w/accounts/${account.id}/documents`}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        Upload identity documents
                      </Button>
                    )}
                    {showCompleteAccBtn && account.accountType ? (
                      <Button
                        width="max-content"
                        outline
                        to={getPathToEditAccount(account, true)}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                      >
                        Complete account
                      </Button>
                    ) : null}
                  </>
                );
              },
            },
            createdAt: {
              label: "Date Created",
              renderer: Renderers.Date,
              sortName: "created-at",
            },
          }}
          noRecordsContent={
            <InvestorPortalEmptyStatus
              title={"You have no accounts available"}
              subTitle={
                "Please create an account so you can make an an investment."
              }
              buttonTo={buildRouteUrl(createAccountRoutes.selectAccount)}
              buttonLabel={"Add new account"}
              imgSrc={"/images/investor-portal/empty-status/accounts.svg"}
            />
          }
          onRowAction={(_, row) => {
            history.push(`/accounts/${row.id}`);
          }}
          defaultSorting={DEFAULT_SORT_FIELD}
          defaultSortingDirection={DEFAULT_SORT_DIRECTION}
          apiPagination
          apiSortable
          sortable
          scrollOnTopOnPageChange
        />
      </Card>
    </InvestorPortalPageContainer>
  );
};

AccountsPage.getInitialProps = async (props) => {
  const result: AccountsPageServerInitialProps = {};

  const accountModel = apiAccountModel({
    ...props,
    forcePlacement: { getMany: false },
  });
  result.serverDataAccounts = await accountModel.getPagedMany({
    pageSize: DEFAULT_PAGE_SIZE,
    sort: DEFAULT_SORT_FIELD,
    sortDirection: DEFAULT_SORT_DIRECTION,
  });
  return result;
};

AccountsPage.getChunkName = () => "investor-portal-3-accounts-page";

export default AccountsPage;
